import React, { useState, useContext, useEffect, useMemo } from "react";
import { Tabs, Select, Collapse, Spin, Progress, Table } from "antd";
import "./conopsfacilitydetails.css"
import { ConopsContext } from "../../conops-hooks/ConopsContext";
import { of, from, Subject } from "rxjs";
import { debounceTime, switchMap, catchError } from "rxjs/operators";
import axios from "axios";
import apiUrlconops from "../../../ApiEndpoint/configconops";
import apiClientconops from "../../../ApiEndpoint/apiClientconops";
import qs from "qs";
import logo from "../../../assets/veoliaLogo.png";


const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Option } = Select;


const ConopsFacilityDetails = () => {
    const { facilityTableObject, registerId } = useContext(ConopsContext);
    const facility = facilityTableObject?.[0];

    // Lift programLinks state here to pass to Compliance
    const [programLinks, setProgramLinks] = useState({
        npdes_records: [],
        pwsid_records: [],
    });

    //ProspectIntelligence api Integration
    const [projectData, setProjectData] = useState([]);
    const [loading, setLoading] = useState(true);
    // project detail API integration
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const project$ = of(true).pipe(
            debounceTime(400),
            switchMap(() =>
                from(
                    apiClientconops.get(
                        `${apiUrlconops}/get_opportunity_detail?registry_id=${registerId}`,
                        { signal }
                    )
                ).pipe(
                    catchError((error) => {
                        console.error("Error fetching project details:", error);
                        return of({ data: [] });
                    })
                )
            )
        );

        const subscription = project$.subscribe((response) => {
            console.log(response, "response")
            setProjectData(response?.data?.data?.[0] || null);
            setLoading(false);
        });

        return () => {
            subscription.unsubscribe();
            controller.abort();
        };
    }, [registerId]);




    return (
        <div className="mt-2">
            <h5 className="facility-title"> {facility?.FAC_NAME || "N/A"}</h5>
            <span className="facility-subtitle">
                {facility?.IS_VEOLIA_SITE && (
                    <>
                        <img
                            src={logo}
                            alt="Veolia Logo"
                            className="img-fluid"
                            style={{ height: "45px", marginBottom: "6px" }}
                        />
                        <span className="leftlabel">Customer Site</span>
                    </>
                )}
            </span>
            <Tabs defaultActiveKey="1" type="card">
                <TabPane tab="Overview" key="1">
                    <Overview setProgramLinks={setProgramLinks} />
                </TabPane>
                <TabPane tab="Compliance" key="2">
                    <Compliance programLinks={programLinks} />
                </TabPane>
                <TabPane tab="Enforcement" key="3">
                    <Enforcement programLinks={programLinks} />
                </TabPane>
                {projectData && (
                    <TabPane tab="Prospect Intelligence" key="4">
                        <ProspectIntelligence projectData={projectData} loading={loading} />
                    </TabPane>
                )}
            </Tabs>
        </div>
    );
};

const Overview = ({ setProgramLinks }) => {
    const { facilityTableObject, registerId } = useContext(ConopsContext);
    const facility = facilityTableObject?.[0];

    const [programLinksLocal, setProgramLinksLocal] = useState({
        npdes_records: [],
        pwsid_records: [],
    });

    const facType = facility?.FAC_TYPE || "";
    const showPwsid = facType.includes("WTP");
    const showNpdes = facType.includes("WWTP");

    const fetchTrigger$ = new Subject();

    useEffect(() => {
        if (!facility?.REGISTRY_ID) return;

        const subscription = fetchTrigger$
            .pipe(
                debounceTime(200),
                switchMap(() => {
                    const params = { registry_id: facility.REGISTRY_ID };
                    return from(apiClientconops.get(`${apiUrlconops}/programlinks`, { params })).pipe(
                        switchMap((res) => of(res.data)),
                        catchError(() => of({ npdes_records: [], pwsid_records: [] }))
                    );
                })
            )
            .subscribe((data) => {
                setProgramLinksLocal({
                    npdes_records: data.npdes_records || [],
                    pwsid_records: data.pwsid_records || [],
                });
                // Pass to parent so Compliance can use it
                setProgramLinks({
                    npdes_records: data.npdes_records || [],
                    pwsid_records: data.pwsid_records || [],
                });
            });

        fetchTrigger$.next();
        return () => subscription.unsubscribe();
    }, [facility?.REGISTRY_ID, setProgramLinks]);

    const columns = {
        pwsid: [
            { title: "PWSID", dataIndex: "pwsid" },
            { title: "Operating Status", dataIndex: "operating_status" },
        ],
        npdes: [
            { title: "NPDES_ID", dataIndex: "npdes_id" },
            { title: "Operating Status", dataIndex: "operating_status" },
        ],
    };

    // Progress bar calculation
    const totalFlow = parseFloat(facility?.TOTAL_DESIGN_FLOW_NUMBERS) || 0;
    const actualFlow = parseFloat(facility?.ACTUAL_AVERAGE_FLOW_NUMBERS) || 0;
    const percent = totalFlow > 0 ? (actualFlow / totalFlow) * 100 : 0;

    // Facility Summary
    const [facilitySummary, setFacilitySummary] = useState("");
    const { Message, Citations } = facilitySummary;
    const [isSummaryLoading, setIsSummaryLoading] = useState(true);

    useEffect(() => {
        if (!registerId) return;

        const controller = new AbortController();
        setIsSummaryLoading(true);

        const fetchFacilitySummary = async () => {
            try {
                const response = await apiClientconops.get(`${apiUrlconops}/facility_summary`, {
                    params: { registry_id: registerId },
                    signal: controller.signal,
                });
                setFacilitySummary(response.data || { Message: "No data found." });
            } catch (error) {
                if (error.name === 'AbortError' || error.name === 'CanceledError') {
                    return;
                }
                console.error("Error fetching facility data:", error);
                setFacilitySummary({ Message: "An error occurred. Please contact the Administrator." });
            } finally {
                if (!controller.signal.aborted) {
                    setIsSummaryLoading(false);
                }
            }
        };

        fetchFacilitySummary();

        return () => {
            controller.abort();
        };
    }, [registerId]);

    return (
        <div className="container p-0" style={{ maxHeight: "66vh", overflowY: "scroll" }}>
            <h5 className="facility-title">Detail Summary</h5>

            {isSummaryLoading ? (
                <p className="blinking-text">Loading description...</p>
            ) : (
                <>
                    <p className="site-description">{Message}</p>
                    {Citations && Citations.length > 0 && (
                        <ul>
                            {Citations.map((citation, index) => {
                                const match = citation.match(/\[([^\]]+)\]\(([^)]+)\)/);
                                if (!match) return null;
                                const [_, label, url] = match;
                                return (
                                    <li key={index}>
                                        <span>{label}:</span>{" "}
                                        <a href={url} target="_blank" rel="noopener noreferrer">
                                            {url}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </>
            )}

            <div className="mb-3">
                <h5 className="facility-title">Facility Summary</h5>
                <ul className="facility-summary-list">
                    <li><strong>Street Address:</strong> {facility?.FAC_STREET || "N/A"}</li>
                    <li><strong>Zip Code:</strong> {facility?.FAC_ZIP || "N/A"}</li>
                    <li><strong>City:</strong> {facility?.FAC_CITY || "N/A"}</li>
                    <li><strong>County:</strong> {facility?.FAC_COUNTY || "N/A"}</li>
                    <li><strong>State:</strong> {facility?.STATE_NAME || "N/A"}</li>
                    <li><strong>Facility Type:</strong> {facType.split("_").join(" & ") || "N/A"}</li>
                    <li><strong>Population Density:</strong> {facility?.POPULATION_DENSITY_PER_SQ_MILE || "N/A"}</li>
                </ul>
            </div>

            {/* pwsid table */}
            {showPwsid && programLinksLocal.pwsid_records.length > 0 && (
                <div className="mb-3">
                    <h5 className="facility-title">PWSID Details</h5>
                    <Table
                        columns={columns.pwsid}
                        dataSource={programLinksLocal.pwsid_records.map((r, i) => ({ key: i, ...r }))}
                        pagination={false}
                        scroll={{ y: 200 }}
                    />
                </div>
            )}





            {/* npdes table */}
            {showNpdes && programLinksLocal.npdes_records.length > 0 && (
                <div className="mb-3">
                    <h5 className="facility-title">NPDES Details</h5>
                    <Table
                        columns={columns.npdes}
                        dataSource={programLinksLocal.npdes_records.map((r, i) => ({ key: i, ...r }))}
                        pagination={false}
                        scroll={{ y: 200 }}
                    />
                </div>
            )}

            {totalFlow > 0 && (
                <div className="mb-4">
                    <h5 className="facility-title" style={{ color: "#0078D4" }}>Operational Capacity</h5>
                    <Progress
                        percent={percent}
                        showInfo={false}
                        strokeColor="#8dc63f"
                        trailColor="#d9d9d9"
                        strokeWidth={15}
                        style={{ width: "50%", borderRadius: "8px" }}
                    />
                    <div className="d-flex mt-1">
                        <span>Actual:{actualFlow} MGD</span>
                        <span style={{ paddingLeft: '60px' }}>Total:{totalFlow} MGD</span>
                    </div>
                </div>
            )}
        </div>
    );
};

const Compliance = ({ programLinks }) => {
    const { registerId, facilityTableObject } = useContext(ConopsContext);
    const [complianceDataWTP, setComplianceDataWTP] = useState(null);
    const [complianceDataWWTP, setComplianceDataWWTP] = useState(null);

    const [loadingWTP, setLoadingWTP] = useState(false);
    const [loadingWWTP, setLoadingWWTP] = useState(false);

    const [selectedPwsid, setSelectedPwsid] = useState(
        programLinks?.pwsid_records?.[0]?.pwsid || null
    );
    const [selectedNpdes, setSelectedNpdes] = useState(
        programLinks?.npdes_records?.[0]?.npdes_id || null
    );

    const facType = facilityTableObject?.[0]?.FAC_TYPE || "";

    const handleClearPwsid = () => {
        if (programLinks?.pwsid_records?.length > 0) {
            setSelectedPwsid(programLinks.pwsid_records[0].pwsid);
        } else {
            setSelectedPwsid(null);
            setComplianceDataWTP(null);
        }
    };

    const handleClearNpdes = () => {
        if (programLinks?.npdes_records?.length > 0) {
            setSelectedNpdes(programLinks.npdes_records[0].npdes_id);
        } else {
            setSelectedNpdes(null);
            setComplianceDataWWTP(null);
        }
    };

    useEffect(() => {
        if (programLinks?.pwsid_records?.length) {
            setSelectedPwsid(programLinks.pwsid_records[0].pwsid);
        }
        if (programLinks?.npdes_records?.length) {
            setSelectedNpdes(programLinks.npdes_records[0].npdes_id);
        }
    }, [programLinks]);

    // Fetch WTP compliance
    useEffect(() => {
        if (!selectedPwsid) {
            setComplianceDataWTP(null);
            return;
        }
        const fetchWTP = async () => {
            setLoadingWTP(true);
            try {
                const { data } = await apiClientconops.get(`${apiUrlconops}/pwsid/compliance`, {
                    params: { pwsid: selectedPwsid },
                });
                setComplianceDataWTP(data);
            } catch (err) {
                console.error("Error fetching WTP compliance:", err);
                setComplianceDataWTP(null);
            } finally {
                setLoadingWTP(false);
            }
        };
        fetchWTP();
    }, [selectedPwsid]);

    // Fetch WWTP compliance
    useEffect(() => {
        if (!selectedNpdes || !registerId) {
            setComplianceDataWWTP(null);
            return;
        }

        const fetchWWTP = async () => {
            setLoadingWWTP(true);
            try {
                const { data } = await apiClientconops.get(`${apiUrlconops}/npdes/compliance`, {
                    params: {
                        npdes_id: selectedNpdes,
                        registry_id: registerId,
                    },
                });
                setComplianceDataWWTP(data);
            } catch (err) {
                console.error("Error fetching WWTP compliance:", err);
                setComplianceDataWWTP(null);
            } finally {
                setLoadingWWTP(false);
            }
        };

        fetchWWTP();
    }, [selectedNpdes, registerId]);

    const displayValue = (val) => (val !== null && val !== undefined ? val : "N/A");

    // ✅ Check if both PWSID and NPDES are missing
    const noProgramLinks =
        (!programLinks?.pwsid_records?.length || !facType.includes("WTP")) &&
        (!programLinks?.npdes_records?.length || !facType.includes("WWTP"));

    return (
        <div className="container-fluid" style={{ maxHeight: "66vh", overflowY: "scroll" }}>
            {noProgramLinks ? (
                <p className="text-center" style={{ marginTop: "2rem", fontWeight: "bold" }}>
                    No PWSID or NPDES records available for this Facility.
                </p>
            ) : (
                <>
                    {/* WTP Section */}
                    {facType.includes("WTP") && programLinks?.pwsid_records?.length > 0 && (
                        <>
                            <div className="row mb-3" style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Select
                                    className="filter-selects"
                                    showSearch
                                    allowClear
                                    style={{ width: "12rem" }}
                                    value={selectedPwsid}
                                    onChange={(val) => setSelectedPwsid(val)}
                                    onClear={handleClearPwsid}
                                >
                                    {/* {programLinks.pwsid_records.map((rec) => (
                                        <Option key={rec.pwsid} value={rec.pwsid}>
                                            {rec.pwsid}
                                        </Option>
                                    ))} */}
                                    {[...new Map(
                                        programLinks.pwsid_records.map((rec) => [rec.pwsid, rec])
                                    ).values()].map((rec) => (
                                        <Option key={rec.pwsid} value={rec.pwsid}>
                                            {rec.pwsid}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div className="row p-2">
                                {loadingWTP ? (
                                    <Spin />
                                ) : (
                                    <>
                                        <h5 className="facility-title">Compliance Summary (WTP)</h5>
                                        <ul className="facility-summary-list">
                                            <li>
                                                <strong>Significant Violation:</strong>{" "}
                                                {displayValue(
                                                    complianceDataWTP?.Significant_Violations_Last5Y > 0
                                                        ? "Yes"
                                                        : "No"
                                                )}
                                            </li>
                                            <li>
                                                <strong>Date of Last Significant Violation:</strong>{" "}
                                                {displayValue(complianceDataWTP?.Last_Significant_Violation_Date)}
                                            </li>
                                            <li>
                                                <strong>Compliance Status:</strong>{" "}
                                                {displayValue(complianceDataWTP?.Compliance_Status)}
                                            </li>
                                            <li>
                                                <strong>Days Since Last Compliance Monitoring:</strong>{" "}
                                                {displayValue(complianceDataWTP?.Days_Since_Last_Compliance_Monitoring)}
                                            </li>
                                            <li>
                                                <strong>Compliance Monitoring Activities (5 yrs):</strong>{" "}
                                                {displayValue(complianceDataWTP?.Compliance_Monitoring_Activities_5yr)}
                                            </li>
                                            <li>
                                                <strong>Quarters with Non-Compliance (5 yrs):</strong>{" "}
                                                {displayValue(complianceDataWTP?.Quarters_with_NonCompliance_5yr)}
                                            </li>
                                            <li>
                                                <strong>Last Monitoring Activity Date:</strong>{" "}
                                                {displayValue(complianceDataWTP?.Last_Monitoring_Activity_Date)}
                                            </li>
                                        </ul>
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {/* WWTP Section */}
                    {facType.includes("WWTP") && programLinks?.npdes_records?.length > 0 && (
                        <>
                            <div className="row mb-3" style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Select
                                    className="filter-selects"
                                    showSearch
                                    allowClear
                                    style={{ width: "12rem" }}
                                    value={selectedNpdes}
                                    onChange={(val) => setSelectedNpdes(val)}
                                    onClear={handleClearNpdes}
                                >
                                    {[...new Map(
                                        programLinks.npdes_records.map((rec) => [rec.npdes_id, rec])
                                    ).values()].map((rec) => (
                                        <Option key={rec.npdes_id} value={rec.npdes_id}>
                                            {rec.npdes_id}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div className="row p-2">
                                {loadingWWTP ? (
                                    <Spin />
                                ) : (
                                    <>
                                        <h5 className="facility-title">Compliance Summary (WWTP)</h5>
                                        <ul className="facility-summary-list">
                                            <li>
                                                <strong>Total Significant Violations (5 yrs):</strong>{" "}
                                                {displayValue(
                                                    complianceDataWWTP?.Total_Significant_Violations_Last5Y > 0
                                                        ? "Yes"
                                                        : "No"
                                                )}
                                            </li>
                                            <li>
                                                <strong>Last Significant Violation Date:</strong>{" "}
                                                {displayValue(complianceDataWWTP?.Last_Significant_Violation_Date)}
                                            </li>
                                            <li>
                                                <strong>Days Since Last Significant Violation:</strong>{" "}
                                                {displayValue(complianceDataWWTP?.Last_Significant_Violation_Days)}
                                            </li>
                                            <li>
                                                <strong>Monitoring Activities (5 yrs):</strong>{" "}
                                                {displayValue(complianceDataWWTP?.QTR_Monitoring_Activities_5Y)}
                                            </li>
                                            <li>
                                                <strong>Last Monitoring Date:</strong>{" "}
                                                {displayValue(complianceDataWWTP?.Last_Monitoring_Date)}
                                            </li>
                                            <li>
                                                <strong>Days Since Last Monitoring:</strong>{" "}
                                                {displayValue(complianceDataWWTP?.Days_Since_Last_Monitoring)}
                                            </li>
                                        </ul>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};



const Enforcement = ({ programLinks }) => {
    const { registerId, facilityTableObject } = useContext(ConopsContext);
    const facType = facilityTableObject?.[0]?.FAC_TYPE || "";

    const [selectedPwsid, setSelectedPwsid] = useState(programLinks?.pwsid_records?.[0]?.pwsid || null);
    const [selectedNpdes, setSelectedNpdes] = useState(programLinks?.npdes_records?.[0]?.npdes_id || null);

    const [enforcementDataWTP, setEnforcementDataWTP] = useState(null);
    const [enforcementDataWWTP, setEnforcementDataWWTP] = useState(null);

    const [loadingWTP, setLoadingWTP] = useState(false);
    const [loadingWWTP, setLoadingWWTP] = useState(false);

    useEffect(() => {
        if (programLinks?.pwsid_records?.length) {
            setSelectedPwsid(programLinks.pwsid_records[0].pwsid);
        }
        if (programLinks?.npdes_records?.length) {
            setSelectedNpdes(programLinks.npdes_records[0].npdes_id);
        }
    }, [programLinks]);

    // Fetch Enforcement data for WTP
    useEffect(() => {
        if (!selectedPwsid) {
            setEnforcementDataWTP(null);
            return;
        }

        const fetchEnforcementWTP = async () => {
            setLoadingWTP(true);
            try {
                const { data } = await apiClientconops.get(`${apiUrlconops}/pwsid/enforcements`, {
                    params: { pwsid: selectedPwsid },
                });
                setEnforcementDataWTP(data);
            } catch (err) {
                console.error("Error fetching WTP enforcement:", err);
                setEnforcementDataWTP(null);
            } finally {
                setLoadingWTP(false);
            }
        };
        fetchEnforcementWTP();
    }, [selectedPwsid]);

    // Fetch Enforcement data for WWTP
    useEffect(() => {
        if (!selectedNpdes) {
            setEnforcementDataWWTP(null);
            return;
        }

        const fetchEnforcementWWTP = async () => {
            setLoadingWWTP(true);
            try {
                const { data } = await apiClientconops.get(`${apiUrlconops}/npdes/enforcements`, {
                    params: { npdes_id: selectedNpdes },
                });
                setEnforcementDataWWTP(data);
            } catch (err) {
                console.error("Error fetching WWTP enforcement:", err);
                setEnforcementDataWWTP(null);
            } finally {
                setLoadingWWTP(false);
            }
        };
        fetchEnforcementWWTP();
    }, [selectedNpdes]);

    const displayValue = (val) => (val !== null && val !== undefined ? val : "N/A");

    const columns = [
        {
            title: "ENFORCEMENT_ID",
            dataIndex: "ENFORCEMENT_ID",
            key: "ENFORCEMENT_ID",
            align: "center",
        },
        {
            title: "ENF_ACTION_TYPE",
            dataIndex: "ENF_ACTION_TYPE",
            key: "ENF_ACTION_TYPE",
            align: "center",
        },
    ];

    const columnsnpdes = [
        {
            title: "NPDES_ID",
            dataIndex: "NPDES_ID",
            key: "NPDES_ID",
            align: "center",
        },
        {
            title: "ACTIVITY_TYPE_CODE",
            dataIndex: "ACTIVITY_TYPE_CODE",
            key: "ACTIVITY_TYPE_CODE",
            align: "center",
        },
    ];

    // ✅ Check if both PWSID and NPDES are missing
    const noProgramLinks =
        (!programLinks?.pwsid_records?.length || !facType.includes("WTP")) &&
        (!programLinks?.npdes_records?.length || !facType.includes("WWTP"));

    return (
        <div className="container-fluid" style={{ maxHeight: "66vh", overflowY: "scroll" }}>
            {noProgramLinks ? (
                <p className="text-center" style={{ marginTop: "2rem", fontWeight: "bold" }}>
                    No PWSID or NPDES records available for this Facility.
                </p>
            ) : (
                <>
                    {/* 🔹 WTP Section */}
                    {facType.includes("WTP") && programLinks?.pwsid_records?.length > 0 && (
                        <>
                            <div className="row mb-3" style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Select
                                    className="filter-selects"
                                    showSearch
                                    allowClear
                                    style={{ width: "12rem" }}
                                    value={selectedPwsid}
                                    onChange={(val) => setSelectedPwsid(val)}
                                    onClear={() => setSelectedPwsid(null)}
                                >
                                    {/* {programLinks.pwsid_records.map((rec) => (
                                        <Option key={rec.pwsid} value={rec.pwsid}>
                                            {rec.pwsid}
                                        </Option>
                                    ))} */}
                                    {[...new Map(
                                        programLinks.pwsid_records.map((rec) => [rec.pwsid, rec])
                                    ).values()].map((rec) => (
                                        <Option key={rec.pwsid} value={rec.pwsid}>
                                            {rec.pwsid}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div className="row p-2">
                                {loadingWTP ? (
                                    <Spin />
                                ) : (
                                    <>
                                        <h5 className="facility-title">Enforcement & Litigation (WTP)</h5>
                                        <ul className="facility-summary-list">
                                            <li>
                                                <strong>Informal Enforcement Action:</strong>{" "}
                                                {displayValue(enforcementDataWTP?.informal_records_last5yr?.length || 0)}
                                            </li>
                                            <li>
                                                <strong>Formal Enforcement Action:</strong>{" "}
                                                {displayValue(enforcementDataWTP?.formal_records_last5yr?.length || 0)}
                                            </li>
                                            <li>
                                                <strong>Date of Last Informal Enforcement Action:</strong>{" "}
                                                {displayValue(enforcementDataWTP?.last_informal_action_date)}
                                            </li>
                                            <li>
                                                <strong>Date of Last Formal Enforcement:</strong>{" "}
                                                {displayValue(enforcementDataWTP?.last_formal_action_date)}
                                            </li>
                                            <li>
                                                <strong>Litigation:</strong>{" "}
                                                {displayValue(enforcementDataWTP?.litigation_enforcements?.length || 0)}
                                            </li>
                                        </ul>

                                        {enforcementDataWTP?.formal_records_last5yr?.length > 0 && (
                                            <div className="mt-4">
                                                <h6 className="facility-title">ENF_ACTION_CATEGORY: Formal</h6>
                                                <Table
                                                    bordered
                                                    size="small"
                                                    pagination={false}
                                                    scroll={{ y: 200 }}
                                                    dataSource={enforcementDataWTP.formal_records_last5yr.map((rec, i) => ({
                                                        key: i,
                                                        ENFORCEMENT_ID: rec.ENFORCEMENT_ID,
                                                        ENF_ACTION_TYPE: rec.ENFORCEMENT_ACTION_TYPE_CODE,
                                                    }))}
                                                    columns={columns}
                                                />
                                            </div>
                                        )}

                                        {enforcementDataWTP?.informal_records_last5yr?.length > 0 && (
                                            <div className="mt-4">
                                                <h6 className="facility-title">ENF_ACTION_CATEGORY: Informal</h6>
                                                <Table
                                                    bordered
                                                    size="small"
                                                    pagination={false}
                                                    scroll={{ y: 200 }}
                                                    dataSource={enforcementDataWTP.informal_records_last5yr.map((rec, i) => ({
                                                        key: i,
                                                        ENFORCEMENT_ID: rec.ENFORCEMENT_ID,
                                                        ENF_ACTION_TYPE: rec.ENFORCEMENT_ACTION_TYPE_CODE,
                                                    }))}
                                                    columns={columns}
                                                />
                                            </div>
                                        )}
                                        {enforcementDataWTP?.litigation_enforcements?.length > 0 && (
                                            <div className="mt-4">
                                                <h6 className="facility-title">ENF_ACTION_CATEGORY: Litigration</h6>
                                                <Table
                                                    bordered
                                                    size="small"
                                                    pagination={false}
                                                    scroll={{ y: 200 }}
                                                    dataSource={enforcementDataWTP.litigation_enforcements.map((rec, i) => ({
                                                        key: i,
                                                        ENFORCEMENT_ID: rec.ENFORCEMENT_ID,
                                                        ENF_ACTION_TYPE: rec.ENFORCEMENT_ACTION_TYPE_CODE,
                                                    }))}
                                                    columns={columns}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {/* 🔹 WWTP Section */}
                    {facType.includes("WWTP") && programLinks?.npdes_records?.length > 0 && (
                        <>
                            <div className="row mb-3 mt-3" style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Select
                                    className="filter-selects"
                                    showSearch
                                    allowClear
                                    style={{ width: "12rem" }}
                                    value={selectedNpdes}
                                    onChange={(val) => setSelectedNpdes(val)}
                                    onClear={() => setSelectedNpdes(null)}
                                >
                                    {/* {programLinks.npdes_records.map((rec) => (
                                        <Option key={rec.npdes_id} value={rec.npdes_id}>
                                            {rec.npdes_id}
                                        </Option>
                                    ))} */}
                                    {[...new Map(
                                        programLinks.npdes_records.map((rec) => [rec.npdes_id, rec])
                                    ).values()].map((rec) => (
                                        <Option key={rec.npdes_id} value={rec.npdes_id}>
                                            {rec.npdes_id}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div className="row p-2">
                                {loadingWWTP ? (
                                    <Spin />
                                ) : (
                                    <>
                                        <h5 className="facility-title">Enforcement & Litigation (WWTP)</h5>
                                        <ul className="facility-summary-list">
                                            <li>
                                                <strong>Informal Enforcement Action:</strong>{" "}
                                                {displayValue(enforcementDataWWTP?.informal_records_last5yr?.length || 0)}
                                            </li>
                                            <li>
                                                <strong>Formal Enforcement Action:</strong>{" "}
                                                {displayValue(enforcementDataWWTP?.formal_records_last5yr?.length || 0)}
                                            </li>
                                            <li>
                                                <strong>Date of Last Informal Enforcement Action:</strong>{" "}
                                                {displayValue(enforcementDataWWTP?.last_informal_action_date)}
                                            </li>
                                            <li>
                                                <strong>Date of Last Formal Enforcement Action:</strong>{" "}
                                                {displayValue(enforcementDataWWTP?.last_formal_action_date)}
                                            </li>
                                            <li>
                                                <strong>Litigation:</strong>{" "}
                                                {displayValue(enforcementDataWWTP?.litigation_records_last5yr?.length || 0)}
                                            </li>
                                        </ul>

                                        {enforcementDataWWTP?.formal_records_last5yr?.length > 0 && (
                                            <div className="mt-4">
                                                <h6 className="facility-title">ACTIVITY_TYPE_CODE: Formal</h6>
                                                <Table
                                                    bordered
                                                    size="small"
                                                    pagination={false}
                                                    scroll={{ y: 200 }}
                                                    dataSource={enforcementDataWWTP.formal_records_last5yr.map((rec, i) => ({
                                                        key: i,
                                                        NPDES_ID: rec.NPDES_ID,
                                                        ACTIVITY_TYPE_CODE: rec.ACTIVITY_TYPE_CODE,
                                                    }))}
                                                    columns={columnsnpdes}
                                                />
                                            </div>
                                        )}

                                        {enforcementDataWWTP?.informal_records_last5yr?.length > 0 && (
                                            <div className="mt-4">
                                                <h6 className="facility-title">ACTIVITY_TYPE_CODE: Informal</h6>
                                                <Table
                                                    bordered
                                                    size="small"
                                                    pagination={false}
                                                    scroll={{ y: 200 }}
                                                    dataSource={enforcementDataWWTP.informal_records_last5yr.map((rec, i) => ({
                                                        key: i,
                                                        NPDES_ID: rec.NPDES_ID,
                                                        ACTIVITY_TYPE_CODE: rec.ACTIVITY_TYPE_CODE,
                                                    }))}
                                                    columns={columnsnpdes}
                                                />
                                            </div>
                                        )}
                                        {enforcementDataWWTP?.litigation_records_last5yr?.length > 0 && (
                                            <div className="mt-4">
                                                <h6 className="facility-title">ACTIVITY_TYPE_CODE: Litigration</h6>
                                                <Table
                                                    bordered
                                                    size="small"
                                                    pagination={false}
                                                    scroll={{ y: 200 }}
                                                    dataSource={enforcementDataWWTP.litigation_records_last5yr.map((rec, i) => ({
                                                        key: i,
                                                        NPDES_ID: rec.NPDES_ID,
                                                        ACTIVITY_TYPE_CODE: rec.ACTIVITY_TYPE_CODE,
                                                    }))}
                                                    columns={columnsnpdes}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

const ProspectIntelligence = ({ loading, projectData }) => {
    return (
        <>
            {loading ? (
                <Spin />
            ) : projectData ? (
                <div className="mb-3">
                    <h5 className="facility-title">Summary</h5>
                    <ul className="facility-summary-list">
                        <li>
                            <strong>Project Name:</strong> {projectData?.Project_Name || "-"}
                        </li>

                        <li>
                            <strong>Address / Zip Code:</strong> {projectData?.Address || "-"}
                        </li>

                        <li>
                            <strong>Latitude & Longitude:</strong>{" "}
                            {projectData?.Latitude}, {projectData?.Longitude}
                        </li>

                        <li>
                            <strong>State & Region:</strong>{" "}
                            {projectData?.State} / {projectData?.Region}
                        </li>

                        <li>
                            <strong>Operator:</strong> {projectData?.Operator || "-"}
                        </li>

                        <li>
                            <strong>Scope:</strong>{" "}
                            {projectData?.Scope_Drinking_Water_or_Wastewater || "-"}
                        </li>

                        <li>
                            <strong>Estimated Contract Revenue:</strong>{" "}
                            ${projectData?.Estimated_Revenue_USD_per_year?.toLocaleString() || "-"}
                        </li>

                        <li>
                            <strong>Total Population Served:</strong>{" "}
                            {projectData?.Total_Population_Served_Non_Overlapping || "-"}
                        </li>

                        <li>
                            <strong>Population Served:</strong>{" "}
                            Water: {projectData?.Population_Served_Water}, Wastewater: {projectData?.Population_Served_Wastewater}
                        </li>

                        <li>
                            <strong>Start Date of Operation Year:</strong>{" "}
                            {projectData?.Start_Date_of_Operations || "-"}
                        </li>

                        <li>
                            <strong>End Date:</strong>{" "}
                            {projectData?.End_Date || "N/A"}
                        </li>

                        <li>
                            <strong>Current Status:</strong>{" "}
                            {projectData?.Status || "-"}
                        </li>
                    </ul>

                    <h5 className="facility-title">Project Description</h5>
                    <p>{projectData?.Project_Description || "No description available."}</p>

                    <h5 className="facility-title">Source of Information</h5>
                    <p>
                        {projectData?.Source ? (
                            <a href={projectData?.Source} target="_blank" rel="noopener noreferrer">
                                {projectData?.Source}
                            </a>
                        ) : (
                            "No source available"
                        )}
                    </p>
                </div>
            ) : (
                <p style={{ textAlign: "center" }}>No project data available.</p>
            )}
        </>
    );

}

export default ConopsFacilityDetails;